import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
    Box, Typography, CircularProgress, Alert, Breadcrumbs, Link, Paper, Stack, Avatar, useTheme, Divider
} from '@mui/material';
import { format, parse as parseDate, isValid } from 'date-fns';
import HomeIcon from '@mui/icons-material/Home';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ChatIcon from '@mui/icons-material/Chat';
import PersonIcon from '@mui/icons-material/Person'; // Default user avatar
import EmailIcon from '@mui/icons-material/Email'; // Icon for email
import AccessTimeIcon from '@mui/icons-material/AccessTime'; // Icon for time
import ReactMarkdown from 'react-markdown'; // Import react-markdown
import remarkGfm from 'remark-gfm'; // Import remark-gfm
import apiClient from '../utils/api';

// Define the Regex pattern as a single string
// Explanation:
// ^                    - Start of string
// (?:[^-]+)           - Agent part (non-capturing group, one or more non-hyphen chars)
// -                    - Literal hyphen
// ([^-@]+@[^-]+)     - Email part (capturing group 1, chars without hyphen/at, '@', chars without hyphen)
// -                    - Literal hyphen
// (\d{14})            - Timestamp part (capturing group 2, exactly 14 digits)
// -                    - Literal hyphen
// (?:[0-9a-fA-F-]+)  - UUID part (non-capturing group, hex chars and hyphens)
// $                    - End of string
const sessionIdPattern = '^(?:[^-]+)-([^-@]+@[^-]+)-(\\d{14})-(?:[0-9a-fA-F-]+)$';
const sessionIdRegex = new RegExp(sessionIdPattern);

const ChatHistoryDetailPage = () => {
    const { chatId } = useParams();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const messagesEndRef = useRef(null);
    const theme = useTheme();

    // Extract summary info from chatId using useMemo for performance
    const chatSummary = useMemo(() => {
        console.log("useMemo triggered with chatId:", chatId);

        if (!chatId) return { email: 'N/A', formattedTimestamp: 'N/A' };

        // Use the RegExp object created from the pattern string
        const match = chatId.match(sessionIdRegex);
        console.log("Regex match result:", match); // Check this log again

        // Explicitly check if match is not null AND has the expected groups
        if (match && match[1] && match[2]) {
            console.log("Regex matched! Group 1 (Email):", match[1], "Group 2 (Timestamp):", match[2]);
            const email = match[1];
            const timestampString = match[2];
            let formattedTimestamp = 'Invalid Date';
            try {
                const parsedDate = parseDate(timestampString, 'yyyyMMddHHmmss', new Date());
                if (isValid(parsedDate)) {
                    formattedTimestamp = format(parsedDate, 'PPp');
                } else {
                     console.warn(`Parsed date from timestamp '${timestampString}' is invalid.`);
                }
            } catch (e) {
                console.error("Error parsing timestamp string:", timestampString, e);
            }
            return { email, formattedTimestamp };
        } else {
            // Log specifically why the condition failed if match is not null
            if (match) {
                 console.warn("Regex matched, but group 1 or 2 is missing/falsy. Match array:", match);
            } else {
                 console.warn("Regex did not match the chatId:", chatId, "Pattern:", sessionIdPattern);
            }
            return { email: 'Unknown', formattedTimestamp: 'Unknown' };
        }
    }, [chatId]); // Dependency array is correct

    useEffect(() => {
        const fetchMessages = async () => {
            if (!chatId) {
                setError('Chat Session ID is missing.');
                setLoading(false);
                return;
            }
            setLoading(true);
            setError('');
            try {
                const response = await apiClient.get(`/chats/${chatId}`);
                // Ensure response.data is an array, default to empty array otherwise
                const messagesData = Array.isArray(response.data) ? response.data : [];
                setMessages(messagesData);
                console.log("Fetched messages:", messagesData);
            } catch (err) {
                console.error("Error fetching chat messages:", err);
                const errorMessage = err.response?.data?.detail || err.message || `Failed to load chat ${chatId}`;
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchMessages();
    }, [chatId]);

    useEffect(() => {
        // Scroll to bottom when messages load/update
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    return (
        <Box sx={{ p: 3 }}>
            {/* Breadcrumbs */}
             <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                 <Link component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center' }} color="inherit" > <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" /> Dashboard </Link>
                 <Link component={RouterLink} to="/chat-analytics" sx={{ display: 'flex', alignItems: 'center' }} color="inherit" > <AnalyticsIcon sx={{ mr: 0.5 }} fontSize="inherit" /> Chat Analytics </Link>
                 <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}> <ChatIcon sx={{ mr: 0.5 }} fontSize="inherit" /> Chat Details </Typography>
             </Breadcrumbs>

             <Typography variant="h5" gutterBottom sx={{ mb: 1 }}> Chat History </Typography>

             {/* --- Summary Section --- */}
             <Paper elevation={1} sx={{ p: 1.5, mb: 3, bgcolor: 'action.hover' }}>
                 <Stack direction="column" spacing={0.5}>
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                         <ChatIcon fontSize="inherit" sx={{ mr: 0.8 }} /> Session ID:
                    </Typography>
                    <Typography variant="body2" sx={{ pl: 3, wordBreak: 'break-all' }}> {chatId} </Typography>

                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mt: 0.5 }}>
                        <EmailIcon fontSize="inherit" sx={{ mr: 0.8 }} /> User Email:
                    </Typography>
                     <Typography variant="body2" sx={{ pl: 3 }}> {chatSummary.email} </Typography>

                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mt: 0.5 }}>
                        <AccessTimeIcon fontSize="inherit" sx={{ mr: 0.8 }} /> Started At:
                    </Typography>
                     <Typography variant="body2" sx={{ pl: 3 }}> {chatSummary.formattedTimestamp} </Typography>
                 </Stack>
             </Paper>
            {/* --- End Summary Section --- */}


            {loading && <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 5 }}/>}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            {!loading && !error && (
                // Chat message container
                <Paper elevation={2} sx={{
                    p: 2,
                    mt: 2,
                    maxHeight: '65vh', // Adjust height if needed due to summary
                    overflowY: 'auto', // Enable scrolling
                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100] // Subtle background
                }}>
                    <Stack spacing={2}>
                        {messages.map((msg, index) => {
                            const isUser = msg.type === 'human';
                            const isAI = msg.type === 'ai';
                            const isError = msg.type === 'error'; // Handle potential error messages

                            // Determine alignment and avatar
                            const alignment = isUser ? 'flex-end' : 'flex-start';
                            const avatar = isUser ? (
                                // Use generic PersonIcon for the user
                                <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 32, height: 32 }}>
                                    <PersonIcon fontSize="small"/>
                                </Avatar>
                            ) : isAI ? (
                                // Adjust Bill's avatar image position
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        // Style the inner img element rendered by Avatar's src prop
                                        '& .MuiAvatar-img': {
                                            objectFit: 'cover', // Ensure the image covers the area
                                            objectPosition: 'center top', // Focus on the top center part
                                        }
                                    }}
                                    src="/bill_white.jpg"
                                    alt="AI"
                                />
                            ) : (
                                <Avatar sx={{ bgcolor: theme.palette.error.main, width: 32, height: 32 }}>!</Avatar> // Error avatar
                            );
                             // Determine message bubble background color
                             const bubbleColor = isUser
                               ? theme.palette.primary.light // User bubble color
                               : isAI
                                 ? theme.palette.background.paper // AI bubble color
                                 : theme.palette.error.light; // Error bubble color


                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: alignment, // Align the whole row
                                        width: '100%',
                                    }}
                                >
                                    {/* Container for Avatar and Bubble */}
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start', // Align avatar top
                                        gap: 1, // Space between avatar and bubble
                                        maxWidth: '80%', // Limit bubble width
                                        // Order avatar based on sender
                                        flexDirection: isUser ? 'row-reverse' : 'row'
                                    }}>
                                         {avatar}
                                         <Paper elevation={1} sx={{
                                             p: 1.5,
                                             borderRadius: isUser ? '20px 20px 5px 20px' : '20px 20px 20px 5px', // Chat bubble shape
                                             bgcolor: bubbleColor,
                                             color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary, // Ensure text is readable
                                             // Style markdown elements within the bubble if needed
                                             '& p': { margin: 0 }, // Remove default paragraph margins
                                             '& a': { color: isUser ? theme.palette.common.white : theme.palette.primary.main }, // Style links
                                             '& code': { backgroundColor: 'rgba(0,0,0,0.08)', px: 0.5, borderRadius: 1 }, // Style inline code
                                             '& pre': { backgroundColor: 'rgba(0,0,0,0.08)', p: 1, borderRadius: 1, overflowX: 'auto' }, // Style code blocks
                                         }}>
                                              {/* Use ReactMarkdown to render content */}
                                             <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                 {typeof msg.content === 'string' ? msg.content : '[Invalid Content]'}
                                             </ReactMarkdown>
                                         </Paper>
                                    </Box>
                                </Box>
                            );
                        })}
                        {/* Element to help scroll to bottom */}
                        <div ref={messagesEndRef} />
                    </Stack>
                    {/* Display if no messages are found */}
                    {messages.length === 0 && !loading && (
                         <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}> No messages found in this chat session. </Typography>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default ChatHistoryDetailPage;